import { useEffect, useRef } from 'react';

/** Refresh only the visible owner. Hiding, navigating or replacing its profile
 * aborts an in-flight request, including transports which complete after abort. */
export function useGuideRefresh(identity: unknown, active: boolean, delay: number, refresh: (signal: AbortSignal) => Promise<void>) {
  const latest = useRef({ delay, refresh }); latest.current = { delay, refresh };
  useEffect(() => {
    if (!active) return;
    let completed = Date.now(), pending: AbortController | undefined, closed = false;
    const check = async () => {
      if (closed || pending || document.hidden || Date.now() - completed < latest.current.delay) return;
      const request = new AbortController(); pending = request;
      try { await latest.current.refresh(request.signal); }
      catch { /* The owning view reports the read failure and chooses retry delay. */ }
      finally { if (!request.signal.aborted) completed = Date.now(); if (pending === request) pending = undefined; }
    };
    const visibility = () => { if (document.hidden) pending?.abort(); else void check(); };
    const timer = window.setInterval(() => void check(), 15_000);
    document.addEventListener('visibilitychange', visibility);
    return () => { closed = true; pending?.abort(); clearInterval(timer); document.removeEventListener('visibilitychange', visibility); };
  }, [identity, active]);
}
